import React, { useMemo, useRef } from 'react';

import isNumber from 'lodash/isNumber';

import {
  fallbackToScientificNotation,
  getChartDropdownOptions,
  getPipelineChartLink,
} from '../../../utils/chart_utils/chart_utils';

import useCSVChartData from '../helpers/useCSVChartData';
import usePrometheusQuery from '../../../utils/hooks/use_prometheus_query';

import { ChartProvider } from '../ChartContext';
import MultiAverageDetailPane from '../details/MultiAverageDetailPane';

import { formatChartDate } from '../../../utils/date_utils';

import { dataArrayAsDataSet, formatSingleLineChart, getParsedChartPoint } from '../helpers/dataset.helpers';
import { ChartWrapper } from '../d3-charts/ChartWrapper';
import LineChart from '../d3-charts/line/D3LineChart';

import { useRefDimensions } from '../d3-charts/d3-helpers';

import COLORS from '../../../constants/colors.constants';

export const useAnomalyCountData = ({
  pipelineID,
  startTimestamp,
  endTimestamp,
  step,
}) => {
  const prometheusResponse = usePrometheusQuery(
    pipelineID === null ? null : `sum(anomaly_counter{pipeline_name="${pipelineID}"})`,
    {
      startTimestamp,
      endTimestamp,
      step,
      queryKey: `anomaly-count-prometheus-${pipelineID}`,
      transformData: data => data?.map(point => getParsedChartPoint(point[1], point[0])),
    },
  );

  return {
    chartData: prometheusResponse?.data,
    isLoading: prometheusResponse?.isLoading,
    message: prometheusResponse?.error || null,
  }
}

export const AnomalyCountChart = ({
  datasets,
  csvData,
  labelFormatString,
  message,
  pipelineID,
  pipelineVersion,
  chartID,
  startDate,
  endDate,
  isLoading = false,
  isEnlarged = false,
}) => {
  const chartTitle = 'Anomaly count';

  const chartLink = useMemo(() => getPipelineChartLink({
    pipelineID, pipelineVersion, chartID,
  }), [chartID, pipelineID, pipelineVersion]);

  const dropdownOptions = getChartDropdownOptions({
    chartLink,
    chartTitle,
    csvData,
    pipelineID,
    pipelineVersion,
    isEnlarged,
  })

  const containerRef = useRef();
  const dimensions = useRefDimensions(containerRef)

  return (
    <ChartProvider
      chartID={chartID}
      datasets={datasets}
      dropdownOptions={dropdownOptions}
      prepareDataset={formatSingleLineChart}
    >
      <ChartWrapper
        containerRef={containerRef}
        chartTitle={chartTitle}
        isLoading={isLoading}
        message={message}
        detailRenderer={MultiAverageDetailPane}
      >
        <LineChart
          chartId="anomaly-count"
          datasets={datasets}
          xTickFormat={formatChartDate(labelFormatString)}
          yTickFormat={fallbackToScientificNotation}
          numberOfXTicks={6}
          numberOfYTicks={6}
          startDate={startDate}
          endDate={endDate}
          containerHeight={dimensions?.height && dimensions.height > 0 ? dimensions.height : 0}
          containerWidth={dimensions?.width && dimensions.width > 0 ? dimensions.width : 0}
          showPoints
        />
      </ChartWrapper>
    </ChartProvider>
  )
}

const AnomalyCountChartWithData = (props) => {
  const chartTitle = 'Anomaly count';

  const {
    pipelineID, startTimestamp, endTimestamp, step,
  } = props;

  const {
    chartData: chartPoints,
    isLoading,
    message,
  } = useAnomalyCountData({
    pipelineID, startTimestamp, endTimestamp, step
  })

  const pointsWithoutNulls = chartPoints.filter(({ y }) => isNumber(y));
  const chartDataset = dataArrayAsDataSet(pointsWithoutNulls, chartTitle);
  const datasets = [
    {
      ...chartDataset,
      id: 'anomaly-count-dataset',
      lineColor: COLORS.brand,
      bgColor: COLORS.border.dark,
      borderColor: COLORS.brand,
    }
  ];

  const csvData = useCSVChartData({
    chartType: 'simple',
    title: chartTitle,
    data: chartPoints,
  })
  
  return (
    <AnomalyCountChart
      {...props}
      datasets={datasets}
      csvData={csvData}
      isLoading={isLoading}
      message={message}
      startDate={new Date(startTimestamp)}
      endDate={new Date(endTimestamp)}
    />
  )
}

export default AnomalyCountChartWithData;
